import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import * as XLSX from 'xlsx';

const IMPORT_TYPES = [
  { id: 'leads', label: 'Leads', desc: 'Import as sales leads', icon: '📋' },
  { id: 'contacts', label: 'Contacts', desc: 'Import as contacts only', icon: '👤' },
  { id: 'companies', label: 'Companies', desc: 'Import as company records', icon: '🏢' },
  { id: 'all', label: 'All (Auto-detect)', desc: 'Creates leads, contacts & companies', icon: '📦' },
];

const EXPORT_TYPES = [
  { id: 'leads', label: 'Leads Only' },
  { id: 'contacts', label: 'Contacts Only' },
  { id: 'companies', label: 'Companies Only' },
  { id: 'all', label: 'All Data' },
];

// Steps: 1=Upload, 2=Choose Type, 3=Map Columns, 4=Confirm, 5=Results
function ImportExportPage() {
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState('leads');
  const [fileInfo, setFileInfo] = useState(null);
  const [parsedData, setParsedData] = useState(null);  // { headers, total_rows, preview, auto_mapping }
  const [rawFileData, setRawFileData] = useState(null); // { csv_content, file_content, file_type }
  const [columnMapping, setColumnMapping] = useState({}); // { crm_field: csv_column }
  const [crmFields, setCrmFields] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Export
  const [exportType, setExportType] = useState('leads');
  const [exportFilters, setExportFilters] = useState({ search: '', status: '', date_range: 'all' });
  const [exporting, setExporting] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch CRM field definitions
  useEffect(() => {
    axios.get(`${API}/import-export/fields`).then(res => setCrmFields(res.data)).catch(() => {});
  }, []);

  // Get active field set based on import type
  const activeFields = useMemo(() => {
    if (importType === 'all' || importType === 'leads') return crmFields.leads || {};
    if (importType === 'contacts') return crmFields.contacts || {};
    if (importType === 'companies') return crmFields.companies || {};
    return {};
  }, [importType, crmFields]);

  // ─── File Upload ───────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.csv', '.xlsx', '.xls'].includes(ext)) {
      setMessage({ type: 'error', text: 'Please select a CSV or Excel file' });
      return;
    }

    setMessage({ type: '', text: '' });
    setFileInfo({ name: file.name, type: ext, size: file.size });

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        let requestData = {};

        if (ext === '.xlsx' || ext === '.xls') {
          const data = new Uint8Array(event.target.result);
          const b64 = btoa(data.reduce((d, byte) => d + String.fromCharCode(byte), ''));
          requestData = { file_content: b64, file_type: ext.replace('.', '') };
        } else {
          const content = event.target.result;
          requestData = { csv_content: content, file_type: 'csv' };
        }

        setRawFileData(requestData);

        // Parse on backend for headers + auto-mapping
        const res = await axios.post(`${API}/import-export/parse`, requestData);
        setParsedData(res.data);
        setColumnMapping(res.data.auto_mapping || {});
        setStep(2); // Move to type selection
      } catch (err) {
        setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to parse file' });
      }
    };

    if (ext === '.xlsx' || ext === '.xls') reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  };

  // ─── Import (chunked for large files) ──────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    setMessage({ type: '', text: '' });
    try {
      // Parse the file locally to get all rows
      const parseRes = await axios.post(`${API}/import-export/parse`, rawFileData);
      const totalRows = parseRes.data.total_rows;

      if (totalRows <= 2000) {
        // Small file: single request
        const res = await axios.post(`${API}/import-export/import`, {
          ...rawFileData,
          import_type: importType,
          column_mapping: columnMapping,
          import_source_name: fileInfo?.name || '',
        });
        setImportResults(res.data);
      } else {
        // Large file: parse locally and send in chunks
        let csvContent = rawFileData.csv_content || '';
        if (rawFileData.file_content) {
          // For Excel files, use the xlsx library to parse client-side
          const binary = atob(rawFileData.file_content);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const wb = XLSX.read(bytes, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          csvContent = XLSX.utils.sheet_to_csv(ws);
        }

        // Parse CSV into rows
        const lines = csvContent.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const allRows = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row = {};
          headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
          allRows.push(row);
        }

        const CHUNK_SIZE = 1000;
        const totalChunks = Math.ceil(allRows.length / CHUNK_SIZE);
        const combinedStats = { leads_created: 0, contacts_created: 0, companies_created: 0, stakeholders_added: 0, leads_skipped_duplicate: 0, errors: [], total_rows: allRows.length };

        for (let c = 0; c < totalChunks; c++) {
          const chunk = allRows.slice(c * CHUNK_SIZE, (c + 1) * CHUNK_SIZE);
          setMessage({ type: 'warning', text: `Importing chunk ${c + 1} of ${totalChunks} (${chunk.length} rows)...` });

          const res = await axios.post(`${API}/import-export/import-chunked`, {
            import_type: importType,
            column_mapping: columnMapping,
            rows: chunk,
            chunk_index: c,
            total_chunks: totalChunks,
            import_source_name: fileInfo?.name || '',
          });

          combinedStats.leads_created += res.data.leads_created || 0;
          combinedStats.contacts_created += res.data.contacts_created || 0;
          combinedStats.companies_created += res.data.companies_created || 0;
          combinedStats.stakeholders_added += res.data.stakeholders_added || 0;
          combinedStats.leads_skipped_duplicate += res.data.leads_skipped_duplicate || 0;
          if (res.data.errors?.length) combinedStats.errors.push(...res.data.errors);
        }

        setImportResults(combinedStats);
      }

      setStep(5);
      const r = importResults || {};
      const parts = [];
      if (r.leads_created > 0) parts.push(`${r.leads_created} Leads`);
      if (r.contacts_created > 0) parts.push(`${r.contacts_created} Contacts`);
      if (r.companies_created > 0) parts.push(`${r.companies_created} Companies`);
      if (r.stakeholders_added > 0) parts.push(`${r.stakeholders_added} Stakeholders`);
      setMessage({ type: 'success', text: parts.length ? `Created ${parts.join(', ')}` : 'Import completed!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  // ─── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.post(`${API}/import-export/export`, {
        export_type: exportType,
        filters: exportFilters,
      });
      const blob = new Blob([res.data.csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename || `export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Exported ${res.data.rows_exported} records` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Export failed' });
    } finally {
      setExporting(false);
    }
  };

  const resetImport = () => {
    setStep(1); setParsedData(null); setRawFileData(null); setFileInfo(null);
    setColumnMapping({}); setImportResults(null); setImportType('leads');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="import-export-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Import / Export</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Bulk import or export your CRM data</p>
      </div>

      {message.text && (
        <div className={`px-4 py-3 rounded-lg flex items-start ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
          : message.type === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
        }`} data-testid="import-export-message">
          <div className="flex-1">{message.text}</div>
          <button onClick={() => setMessage({ type: '', text: '' })} className="ml-2 opacity-60 hover:opacity-100">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ═══ IMPORT SECTION ═══ */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-white">Import Data</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Upload CSV or Excel — map columns — import</p>
            </div>
          </div>

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-slate-400 dark:hover:border-slate-500 transition-colors">
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" id="file-upload" data-testid="file-upload-input" />
                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                  <svg className="w-10 h-10 text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Click to upload</span>
                  <span className="text-xs text-slate-400 mt-1">CSV, XLSX, or XLS</span>
                </label>
              </div>

              {/* Download Sample Template */}
              <button
                onClick={() => {
                  const sampleCSV = `Name,Email,Phone,Organization,Industry,Designation,Location,Lead Source,Deal Value (INR),Remarks,Primary Contact (Yes/No),Assign To (SDR Email)
Prateek Sharma,prateek@acmecorp.com,9876543210,Acme Corp,Technology,CTO,Mumbai,Website,500000,Interested in Contact Center,Yes,sdr@intalk.io
Devarshi Mehta,devarshi@acmecorp.com,9876543211,Acme Corp,Technology,VP Sales,Mumbai,Referral,,Needs demo,No,
Amit Kumar,amit@betainc.com,9876543212,Beta Inc,Finance,Manager,Delhi,LinkedIn,300000,Follow up next week,Yes,
Sneha Patel,sneha@betainc.com,9876543213,Beta Inc,Finance,Procurement,Delhi,Email,,Budget holder,No,
Ravi Gupta,ravi@gammaco.com,9876543214,Gamma Co,Healthcare,Director,Pune,Cold Call,1000000,Enterprise deal,Yes,`;
                  const blob = new Blob([sampleCSV], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'intalk-crm-import-template.csv';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                data-testid="download-template-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Download Sample Template (CSV)
              </button>

              <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">How it works:</p>
                <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-1">
                  <li><strong>1 Lead per Company</strong> — multiple people from same company are grouped automatically</li>
                  <li><strong>Primary Contact = Yes</strong> — that person becomes the Lead; others become Contacts + Stakeholders</li>
                  <li><strong>Assign To</strong> — enter SDR email to auto-assign the lead</li>
                  <li>Required columns: <strong>Name, Email, Organization</strong></li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Choose Import Type */}
          {step === 2 && parsedData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">File: <strong className="text-slate-700 dark:text-slate-200">{fileInfo?.name}</strong></p>
                  <p className="text-xs text-slate-400">{parsedData.total_rows} rows detected</p>
                </div>
                <button onClick={resetImport} className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">Change file</button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">Import as:</label>
                <div className="grid grid-cols-2 gap-2">
                  {IMPORT_TYPES.map(t => (
                    <button key={t.id} onClick={() => setImportType(t.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        importType === t.id
                          ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                          : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400'
                      }`} data-testid={`import-type-${t.id}`}>
                      <span className="text-sm font-semibold">{t.label}</span>
                      <p className={`text-[10px] mt-0.5 ${importType === t.id ? 'opacity-70' : 'text-slate-400'}`}>{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={() => setStep(3)} className="w-full py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors" data-testid="proceed-to-mapping">
                Next: Map Columns
              </button>
            </div>
          )}

          {/* Step 3: Column Mapping */}
          {step === 3 && parsedData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Map Your Columns</p>
                <button onClick={() => setStep(2)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Back</button>
              </div>
              <p className="text-xs text-slate-400">Match your CSV columns to CRM fields. We auto-detected what we could.</p>

              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {Object.entries(activeFields).map(([field, info]) => (
                  <div key={field} className="flex items-center gap-2">
                    <div className="w-1/2">
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        {info.label} {info.required && <span className="text-red-500">*</span>}
                      </span>
                    </div>
                    <div className="w-1/2">
                      <select
                        value={columnMapping[field] || ''}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, [field]: e.target.value || undefined }))}
                        className="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                        data-testid={`mapping-${field}`}
                      >
                        <option value="">-- Skip --</option>
                        {parsedData.headers.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview Table */}
              {parsedData.preview?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-medium text-slate-400 uppercase mb-1">Preview (first {parsedData.preview.length} rows)</p>
                  <div className="overflow-x-auto border border-slate-300 dark:border-slate-600 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-900">
                        <tr>
                          {parsedData.headers.slice(0, 6).map((h, i) => (
                            <th key={i} className="px-2 py-1.5 text-left text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {parsedData.preview.map((row, idx) => (
                          <tr key={`row-${idx}`}>
                            {parsedData.headers.slice(0, 6).map((h) => (
                              <td key={h} className="px-2 py-1.5 text-slate-700 dark:text-slate-300 truncate max-w-[120px]">{row[h] || '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <button onClick={() => setStep(4)} className="w-full py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors" data-testid="proceed-to-confirm">
                Next: Review & Import
              </button>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 4 && parsedData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Confirm Import</p>
                <button onClick={() => setStep(3)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Back to mapping</button>
              </div>

              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">File</span>
                  <span className="font-medium text-slate-800 dark:text-white">{fileInfo?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Import as</span>
                  <span className="font-medium text-slate-800 dark:text-white capitalize">{importType}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Total rows</span>
                  <span className="font-medium text-slate-800 dark:text-white">{parsedData.total_rows}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 dark:text-slate-400">Mapped fields</span>
                  <span className="font-medium text-slate-800 dark:text-white">{Object.values(columnMapping).filter(Boolean).length}</span>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p><strong>Smart grouping:</strong> Records are grouped by company name — 1 Lead per company (primary contact becomes the lead).</p>
                <p>Non-primary contacts are saved as Contacts + Stakeholders on the company.</p>
                <p>Existing companies and contacts are updated, not duplicated.</p>
                <p>Optional columns: <strong>Primary Contact (Yes/No)</strong>, <strong>Assign To (SDR Email)</strong></p>
              </div>

              <div className="flex gap-3">
                <button onClick={resetImport} className="flex-1 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                  Cancel
                </button>
                <button onClick={handleImport} disabled={importing} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="confirm-import-btn">
                  {importing && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>}
                  {importing ? 'Importing...' : `Import ${parsedData.total_rows} Records`}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {step === 5 && importResults && (
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Import Summary</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Leads', value: importResults.leads_created, color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' },
                  { label: 'Contacts', value: importResults.contacts_created, color: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' },
                  { label: 'Companies', value: importResults.companies_created, color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' },
                  { label: 'Stakeholders', value: importResults.stakeholders_added, color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' },
                  { label: 'Duplicates Avoided', value: importResults.duplicates_avoided, color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' },
                  { label: 'Duplicate Leads Skipped', value: importResults.leads_skipped_duplicate, color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300' },
                  { label: 'Contacts Updated', value: importResults.contacts_updated, color: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300' },
                ].filter(s => s.value > 0 || ['Leads', 'Contacts', 'Companies'].includes(s.label)).map(s => (
                  <div key={s.label} className={`rounded-lg p-3 text-center ${s.color}`}>
                    <p className="text-2xl font-bold">{s.value || 0}</p>
                    <p className="text-[10px] uppercase tracking-wide mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {importResults.errors?.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-1">Errors ({importResults.errors.length})</p>
                  <ul className="text-[10px] text-red-600 dark:text-red-400 space-y-0.5">
                    {importResults.errors.map((e, i) => <li key={`err-${i}-${e.slice(0,20)}`}>{e}</li>)}
                  </ul>
                </div>
              )}

              {importResults.row_errors?.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">Validation Errors ({importResults.row_errors.length} rows skipped)</p>
                  <div className="space-y-1">
                    {importResults.row_errors.slice(0, 20).map((re, i) => (
                      <div key={`rerr-${re.row}-${i}`} className="flex items-start gap-2 text-[10px]">
                        <span className="font-mono text-amber-600 dark:text-amber-400 whitespace-nowrap">Row {re.row}:</span>
                        <span className="text-amber-700 dark:text-amber-300">{re.name || re.email || 'Unknown'} — {re.errors?.join('; ')}</span>
                      </div>
                    ))}
                    {importResults.row_errors.length > 20 && <p className="text-[10px] text-amber-500">...and {importResults.row_errors.length - 20} more</p>}
                  </div>
                </div>
              )}

              <button onClick={resetImport} className="w-full py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors" data-testid="import-new-file">
                Import Another File
              </button>

              {/* Deep-link CTAs — jump straight to the just-imported records
                  so admins never wonder "did the import actually work?"        */}
              {importResults.import_batch_id && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1" data-testid="import-deeplinks">
                  {importResults.leads_created > 0 && (
                    <Link
                      to={`/leads?batch=${importResults.import_batch_id}`}
                      className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                      data-testid="deeplink-view-leads"
                    >
                      View {importResults.leads_created} imported Leads →
                    </Link>
                  )}
                  {importResults.contacts_created > 0 && (
                    <Link
                      to={`/contacts?batch=${importResults.import_batch_id}`}
                      className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors"
                      data-testid="deeplink-view-contacts"
                    >
                      View {importResults.contacts_created} imported Contacts →
                    </Link>
                  )}
                  {(importResults.companies_created > 0 || importResults.stakeholders_added > 0) && (
                    <Link
                      to={`/accounts?mode=directory&batch=${importResults.import_batch_id}`}
                      className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors"
                      data-testid="deeplink-view-companies"
                    >
                      View {importResults.companies_created} imported Companies →
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ EXPORT SECTION ═══ */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" /></svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-white">Export Data</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Download filtered data as CSV</p>
            </div>
          </div>

          {/* Export Type */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">What to export:</label>
              <div className="flex flex-wrap gap-2">
                {EXPORT_TYPES.map(t => (
                  <button key={t.id} onClick={() => setExportType(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      exportType === t.id
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                        : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400'
                    }`} data-testid={`export-type-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-3">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Apply Filters (optional):</label>
              <input
                type="text"
                value={exportFilters.search}
                onChange={(e) => setExportFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by name, email, company..."
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                data-testid="export-search-filter"
              />
              <select
                value={exportFilters.date_range}
                onChange={(e) => setExportFilters(prev => ({ ...prev, date_range: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                data-testid="export-date-range-filter">
                <option value="all">All time</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="6m">Last 6 months</option>
                <option value="1y">Last 1 year</option>
                <option value="2y">Last 2 years</option>
                <option value="3y">Last 3 years</option>
              </select>
              {(exportType === 'leads' || exportType === 'all') && (
                <select value={exportFilters.status} onChange={(e) => setExportFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" data-testid="export-status-filter">
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="converted">Converted</option>
                  <option value="closed_won">Won</option>
                  <option value="closed_lost">Lost</option>
                </select>
              )}
            </div>

            <button onClick={handleExport} disabled={exporting}
              className="w-full py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-200 disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="export-btn">
              {exporting && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>}
              {exporting ? 'Exporting...' : `Export ${exportType === 'all' ? 'All Data' : exportType} to CSV`}
            </button>

            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg p-3">
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1">Export includes:</p>
              <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
                <li>Unified 39-column sheet — Contact + Company + Lead joined per row</li>
                <li>Deduped rows + &quot;Contacted Person(s)&quot; column</li>
                <li>Filtered by your selected date range and search</li>
                <li>CSV format — ready for Excel or Google Sheets</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportExportPage;
